import React, { useEffect, useRef, useState, memo } from 'react';
import { usePDF } from '../../context/PDFContext';
import { useUI } from '../../context/UIContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAppActions } from '../../context/AppContext';
import { IS_APPLE_TOUCH, blockStylusScroll } from '../../utils/applePencil';

const PDFHighlightBrush = memo(({
    pageNum,
    width,
    height,
    zoomLevel = 1,
    isResizing = false
}) => {
    const { brushHighlights: existingHighlights } = usePDF();
    const { tool, highlightBrushColor: selectedColor, hoveredAnnotationId } = useUI();
    const { setSnippets, setConnections, setIsDirty, selectedItem } = useWorkspace();
    const { handleBrushHighlightCreate: onHighlightCreate } = useAppActions();
    const isActive = tool === "highlight-brush";
    const canvasRef = useRef(null);

    // iPad: stop the Apple Pencil from scrolling the PDF while highlighting
    useEffect(() => {
        if (!IS_APPLE_TOUCH || !isActive) return;
        return blockStylusScroll(canvasRef.current);
    }, [isActive]);
    const [isDrawing, setIsDrawing] = useState(false);
    const currentPathRef = useRef([]);

    // 🎨 Render highlights for this page
    useEffect(() => {
        if (isResizing) return; // 🚀 OPTIMIZATION: Skip expensive re-draw during drag to fix flickering

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.scale(zoomLevel, zoomLevel);
        ctx.clearRect(0, 0, width, height);

        // Filter highlights for THIS page
        const pageHighlights = existingHighlights.filter(h => h.pageNum === pageNum);

        pageHighlights.forEach(highlight => {
            if (!highlight.path || highlight.path.length < 2) return;

            const isHovered = highlight.id === hoveredAnnotationId;
            const lineWidth = isHovered ? (highlight.brushWidth || 20) + 6 : (highlight.brushWidth || 20);
            const alpha = isHovered ? 0.85 : 0.5;

            // Offscreen canvas prevents alpha compounding at self-intersections
            const off = document.createElement('canvas');
            off.width = canvas.width;
            off.height = canvas.height;
            const offCtx = off.getContext('2d');
            offCtx.scale(zoomLevel, zoomLevel);
            offCtx.beginPath();
            offCtx.strokeStyle = highlight.color;
            offCtx.lineWidth = lineWidth;
            offCtx.lineCap = 'round';
            offCtx.lineJoin = 'round';
            if (isHovered) {
                offCtx.shadowColor = highlight.color;
                offCtx.shadowBlur = 20;
            }
            highlight.path.forEach((point, i) => {
                const x = point.xPct * width;
                const y = point.yPct * height;
                if (i === 0) offCtx.moveTo(x, y);
                else offCtx.lineTo(x, y);
            });
            offCtx.stroke();

            ctx.globalAlpha = alpha;
            ctx.drawImage(off, 0, 0, width, height);
            ctx.globalAlpha = 1;
        });
        ctx.restore();
    }, [existingHighlights, pageNum, width, height, zoomLevel, isResizing, hoveredAnnotationId]);

    // Helper to get cleaner coordinates — pointer events always have clientX/Y directly
    const getCoordinates = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) / zoomLevel,
            y: (e.clientY - rect.top) / zoomLevel
        };
    };

    // Handle pointer events for drawing (pen/mouse only — touch passes through for scrolling)
    const handleStart = (e) => {
        if (!isActive) return;
        if (e.pointerType === 'touch') return;
        e.preventDefault();
        e.stopPropagation();
        canvasRef.current.setPointerCapture(e.pointerId);
        const { x, y } = getCoordinates(e);
        currentPathRef.current = [{ x, y }];
        setIsDrawing(true);
    };

    const handleMove = (e) => {
        if (!isDrawing) return;
        if (e.pointerType === 'touch') return;
        // Missed pointerup (interrupted Pencil stroke) — finish instead of drawing on hover
        if (e.buttons === 0) { handleEnd(e); return; }

        if (e.cancelable) e.preventDefault();
        e.stopPropagation();

        const { x, y } = getCoordinates(e);
        currentPathRef.current = [...currentPathRef.current, { x, y }];
        const currentPath = currentPathRef.current;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        ctx.save();
        ctx.scale(zoomLevel, zoomLevel);
        ctx.clearRect(0, 0, width, height);

        // 1. Redraw all saved highlights for this page
        const pageHighlights = existingHighlights.filter(h => h.pageNum === pageNum);
        pageHighlights.forEach(highlight => {
            if (!highlight.path || highlight.path.length < 2) return;

            // Use offscreen canvas per highlight to prevent alpha compounding
            const off = document.createElement('canvas');
            off.width = canvas.width;
            off.height = canvas.height;
            const offCtx = off.getContext('2d');
            offCtx.scale(zoomLevel, zoomLevel);
            offCtx.beginPath();
            offCtx.strokeStyle = highlight.color;
            offCtx.lineWidth = highlight.brushWidth || 20;
            offCtx.lineCap = 'round';
            offCtx.lineJoin = 'round';
            highlight.path.forEach((point, i) => {
                const px = point.xPct * width;
                const py = point.yPct * height;
                if (i === 0) offCtx.moveTo(px, py);
                else offCtx.lineTo(px, py);
            });
            offCtx.stroke();
            ctx.globalAlpha = 0.5;
            ctx.drawImage(off, 0, 0, width, height);
            ctx.globalAlpha = 1;
        });

        // 2. Draw current in-progress stroke via offscreen canvas to prevent alpha compounding
        if (currentPath.length >= 2) {
            const off = document.createElement('canvas');
            off.width = canvas.width;
            off.height = canvas.height;
            const offCtx = off.getContext('2d');
            offCtx.scale(zoomLevel, zoomLevel);
            offCtx.beginPath();
            offCtx.strokeStyle = selectedColor;
            offCtx.lineWidth = 20;
            offCtx.lineCap = 'round';
            offCtx.lineJoin = 'round';
            currentPath.forEach((point, i) => {
                if (i === 0) offCtx.moveTo(point.x, point.y);
                else offCtx.lineTo(point.x, point.y);
            });
            offCtx.stroke();
            ctx.globalAlpha = 0.5;
            ctx.drawImage(off, 0, 0, width, height);
            ctx.globalAlpha = 1;
        }

        ctx.restore();
    };

    const handleEnd = (e) => {
        if (!isDrawing) return;

        const currentPath = currentPathRef.current;
        if (currentPath.length >= 2) {
            // Convert to normalized coordinates
            const normalizedPath = currentPath.map(point => ({
                xPct: point.x / width,
                yPct: point.y / height
            }));

            const highlight = {
                id: `brush-${Date.now()}`,
                pageNum: pageNum,
                color: selectedColor,
                path: normalizedPath,
                brushWidth: 20
            };

            onHighlightCreate(highlight);

            // Auto-connect to selected workspace note if one is active
            if (selectedItem && selectedItem.id && selectedItem.type !== 'anchor') {
                const cx = normalizedPath.reduce((s, p) => s + p.xPct, 0) / normalizedPath.length;
                const cy = normalizedPath.reduce((s, p) => s + p.yPct, 0) / normalizedPath.length;
                const anchorId = `anchor-brush-${Date.now()}`;
                const anchor = {
                    id: anchorId,
                    type: 'anchor',
                    x: -1000,
                    y: -1000,
                    pageNum: pageNum,
                    xPct: cx,
                    yPct: cy,
                    text: `Brush highlight p.${pageNum}`,
                };
                setSnippets(prev => [...prev, anchor]);
                setConnections(prev => [...prev, { from: String(selectedItem.id), to: anchorId }]);
                setIsDirty(true);
            }
        }

        currentPathRef.current = [];
        setIsDrawing(false);
    };

    return (
        <canvas
            ref={canvasRef}
            className="brush-highlight-canvas"
            width={width * zoomLevel}
            height={height * zoomLevel}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: width + 'px',
                height: height + 'px',
                pointerEvents: isActive ? 'auto' : 'none',
                zIndex: isActive ? 10 : 2,
                cursor: isActive ? 'crosshair' : 'default',
                userSelect: 'none',
                // iPad: fingers keep scrolling the PDF, the Pencil highlights (blockStylusScroll)
                touchAction: isActive && !IS_APPLE_TOUCH ? 'none' : 'auto',
            }}
            onPointerDown={handleStart}
            onPointerMove={handleMove}
            onPointerUp={handleEnd}
            onPointerCancel={handleEnd}
        />
    );
});

export default PDFHighlightBrush;
